import {
  autoUpdate,
  FlipOptions,
  OffsetOptions,
  Placement as PlacementType,
  ShiftOptions,
  arrow as uiArrow,
  flip as uiFlip,
  offset as uiOffset,
  shift as uiShift,
  useClick,
  UseClickProps,
  useDismiss,
  UseDismissProps,
  useFloating,
  UseFloatingOptions,
  UseFloatingReturn,
  useFocus,
  UseFocusProps,
  useHover,
  UseHoverProps,
  useInteractions,
  useRole,
  UseRoleProps,
  useTransitionStatus,
  UseTransitionStatusProps,
  useTransitionStyles,
  UseTransitionStylesProps,
} from '@floating-ui/react';

/**
 * Options for the usePopover hook.
 *
 * @param offset - The offset options for the popover - https://floating-ui.com/docs/offset
 * @param shift - The shift options for the popover - https://floating-ui.com/docs/shift
 * @param flip - The flip options for the popover - https://floating-ui.com/docs/flip
 * @param placement - The placement of the popover - https://floating-ui.com/docs/arrow#placement
 * @param strategy - The strategy of the popover - https://floating-ui.com/docs/usefloating#strategy
 * @param transform - Whether to use transform for positioning - https://floating-ui.com/docs/usefloating#transform
 * @param onOpenChange - The callback for when the popover opens or closes - https://floating-ui.com/docs/usefloating#onopenchange
 * @param isOpen - The open state of the floating element to synchronize with the isPositioned value. - https://floating-ui.com/docs/usefloating#open
 * @param arrowPadding - The padding between the arrow element and the floating element edges - https://floating-ui.com/docs/arrow#padding
 * @param arrowRef - The ref for the arrow element - https://floating-ui.com/docs/arrow
 * @param anchorRef - The ref for the anchor element - https://floating-ui.com/docs/usefloating#elements
 * @param popoverRef - The ref for the popover element - https://floating-ui.com/docs/usefloating#elements
 * @returns The refs, floatingStyles, and context for the popover - https://floating-ui.com/docs/usefloating#return-value
 */
export interface UsePopoverOptions {
  /**
   * The offset options for the popover - https://floating-ui.com/docs/offset.
   */
  offset?: OffsetOptions;

  /**
   * The shift options for the popover - https://floating-ui.com/docs/shift.
   */
  shift?: ShiftOptions;

  /**
   * The flip options for the popover - https://floating-ui.com/docs/flip.
   */
  flip?: FlipOptions;

  /**
   * The placement type for the popover - https://floating-ui.com/docs/arrow#placement.
   */
  placement: PlacementType;

  /**
   * The strategy for positioning the popover. Can be either 'absolute' or 'fixed' - https://floating-ui.com/docs/usefloating#strategy.
   */
  strategy?: 'absolute' | 'fixed';

  /**
   * Determines whether to apply a CSS transform to the popover - https://floating-ui.com/docs/usefloating#transform.
   */
  transform?: boolean;

  /**
   * Callback function that is called when the open state of the popover changes - https://floating-ui.com/docs/usefloating#onopenchange.
   */
  onOpenChange?: UseFloatingOptions['onOpenChange'];

  /**
   * The open state of the floating element to synchronize with the isPositioned value. - https://floating-ui.com/docs/usefloating#open.
   */
  isOpen?: boolean;

  /** The padding between the arrow element and the floating element edges - https://floating-ui.com/docs/arrow#padding */
  arrowPadding?: number;

  /**
   * Reference to the arrow element of the popover - https://floating-ui.com/docs/arrow.
   */
  arrowRef?: React.MutableRefObject<SVGSVGElement>;

  /**
   * Reference to the anchor element that the popover is attached to - https://floating-ui.com/docs/usefloating#elements.
   */
  anchorRef?: React.RefObject<HTMLElement>;

  /**
   * Reference to the popover element - https://floating-ui.com/docs/usefloating#elements.
   */
  popoverRef?: React.RefObject<HTMLElement>;
}

/**
 * UsePopover Return
 *
 * The return value of the usePopover hook.
 *
 * @returns The refs, floatingStyles, and context for the popover - https://floating-ui.com/docs/usefloating#return-value.
 */
export interface UsePopoverReturn {
  /**
   * The refs for the popover - https://floating-ui.com/docs/useFloating#refs.
   */
  refs: UseFloatingReturn['refs'];

  /**
   * The floating styles for the popover - https://floating-ui.com/docs/useFloating#floatingstyles.
   */
  floatingStyles: UseFloatingReturn['floatingStyles'];

  /**
   * The context for the popover - https://floating-ui.com/docs/useFloating#context.
   */
  context: UseFloatingReturn['context'];
}

/**
 * UsePopover Hook
 *
 * @param offset - The offset options for the popover - https://floating-ui.com/docs/offset
 * @param shift - The shift options for the popover - https://floating-ui.com/docs/shift
 * @param flip - The flip options for the popover - https://floating-ui.com/docs/flip
 * @param placement - The placement of the popover - https://floating-ui.com/docs/arrow#placement
 * @param strategy - The strategy of the popover - https://floating-ui.com/docs/usefloating#strategy
 * @param transform - Whether to use transform for positioning - https://floating-ui.com/docs/usefloating#transform
 * @param onOpenChange - The callback for when the popover opens or closes - https://floating-ui.com/docs/usefloating#onopenchange
 * @param isOpen - The open state of the floating element to synchronize with the isPositioned value. - https://floating-ui.com/docs/usefloating#open
 * @param arrowPadding - The padding between the arrow element and the floating element edges - https://floating-ui.com/docs/arrow#padding
 * @param arrowRef - The ref for the arrow element - https://floating-ui.com/docs/arrow
 * @param anchorRef - The ref for the anchor element - https://floating-ui.com/docs/usefloating#elements
 * @param popoverRef - The ref for the popover element - https://floating-ui.com/docs/usefloating#elements
 * @returns refs, floatingStyles, and context for the popover - https://floating-ui.com/docs/usefloating#return-value
 */
export const usePopover = ({
  offset,
  shift,
  flip,
  placement,
  strategy,
  transform,
  onOpenChange,
  isOpen,
  arrowRef,
  arrowPadding,
  anchorRef,
  popoverRef,
}: UsePopoverOptions): UsePopoverReturn => {
  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      uiOffset(offset),
      uiShift(shift),
      uiFlip(flip),
      arrowRef && uiArrow({ element: arrowRef, padding: arrowPadding }),
    ],
    placement,
    strategy,
    open: isOpen,
    transform,
    onOpenChange,
    elements: {
      reference: anchorRef?.current,
      floating: popoverRef?.current,
    },
    whileElementsMounted: autoUpdate,
  });

  return { refs, floatingStyles, context };
};

/**
 * Popover Interactions Options
 *
 * @param context - The context of the popover - https://floating-ui.com/docs/useFloating#context
 * @param hoverOptions - The hover options for the popover - https://floating-ui.com/docs/usehover
 * @param clickOptions - The click options for the popover - https://floating-ui.com/docs/useclick
 * @param focusOptions - The focus options for the popover - https://floating-ui.com/docs/usefocus
 * @param dismissOptions - The dismiss options for the popover - https://floating-ui.com/docs/usedismiss
 * @param roleOptions - The role options for the popover - https://floating-ui.com/docs/userole
 * @returns The getReferenceProps and getFloatingProps functions for the popover
 */
export interface UsePopoverInteractionsOptions {
  /**
   * The context of the popover - https://floating-ui.com/docs/useFloating#context.
   */
  context: UseFloatingReturn['context'];

  /**
   * The hover options for the popover - https://floating-ui.com/docs/usehover.
   */
  hoverOptions?: UseHoverProps;

  /**
   * The click options for the popover - https://floating-ui.com/docs/useclick.
   */
  clickOptions?: UseClickProps;

  /**
   * The focus options for the popover - https://floating-ui.com/docs/usefocus.
   */
  focusOptions?: UseFocusProps;

  /**
   * The dismiss options for the popover - https://floating-ui.com/docs/usedismiss.
   */
  dismissOptions?: UseDismissProps;

  /**
   * The role options for the popover - https://floating-ui.com/docs/userole.
   */
  roleOptions?: UseRoleProps;
}

/**
 * Use Interactions Return
 *
 * The return value of the useInteractions hook.
 * @returns The getReferenceProps, getFloatingProps, and getItemProps functions for the interactions.
 */
interface UseInteractionsReturn {
  /**
   * Returns the props and event handlers for the reference element.
   */
  getReferenceProps(userProps?: React.HTMLProps<Element>): Record<string, unknown>;

  /**
   * Returns the props and event handlers for the floating element.
   */
  getFloatingProps(userProps?: React.HTMLProps<HTMLElement>): Record<string, unknown>;

  /**
   * Returns the props and event handlers for the item element.
   */
  getItemProps(userProps?: React.HTMLProps<HTMLElement>): Record<string, unknown>;
}

/**
 * Use Popover Interactions Return
 *
 * The return value of the usePopoverInteractions hook.
 * @returns The getReferenceProps and getFloatingProps functions for the popover.
 */
export interface UsePopoverInteractionsReturn {
  /**
   * Returns the props and event handlers for the reference element.
   */
  getReferenceProps: UseInteractionsReturn['getReferenceProps'];

  /**
   * Returns the props and event handlers for the floating element.
   */
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
}

/**
 * Use Popover Interactions Hook
 *
 * @param context - The context of the popover - https://floating-ui.com/docs/useFloating#context
 * @param clickOptions - The click options for the popover - https://floating-ui.com/docs/useclick
 * @param dismissOptions - The dismiss options for the popover - https://floating-ui.com/docs/usedismiss
 * @param focusOptions - The focus options for the popover - https://floating-ui.com/docs/usefocus
 * @param hoverOptions - The hover options for the popover - https://floating-ui.com/docs/usehover
 * @param roleOptions - The role options for the popover - https://floating-ui.com/docs/userole
 * @returns The getReferenceProps and getFloatingProps functions for the popover
 */
export const usePopoverInteractions = ({
  context,
  clickOptions,
  dismissOptions,
  focusOptions,
  hoverOptions,
  roleOptions,
}: UsePopoverInteractionsOptions): UsePopoverInteractionsReturn => {
  const click = useClick(context, clickOptions);
  const dismiss = useDismiss(context, dismissOptions);
  const focus = useFocus(context, focusOptions);
  const hover = useHover(context, hoverOptions);
  const role = useRole(context, roleOptions);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    focus,
    hover,
    role,
  ]);

  return { getReferenceProps, getFloatingProps };
};

/**
 * Use Transitions Props
 * @param context - The context of the popover - https://floating-ui.com/docs/useFloating#context
 * @param transitionStylesConfig - It handles the transition styles through javascript - https://floating-ui.com/docs/usetransition#usetransitionstyles
 * @param transitionStatusConfig - It handles the transition duration and styles through css - https://floating-ui.com/docs/usetransition#usetransitionstatus
 */
export interface UseTransitionsProps {
  context: UseFloatingReturn['context'];
  transitionStylesConfig?: UseTransitionStylesProps;
  transitionStatusConfig?: UseTransitionStatusProps;
}

/**
 * Use Transitions Hook
 * @param context - The context of the popover - https://floating-ui.com/docs/useFloating#context
 * @param transitionStylesConfig - It handles the transition styles through javascript - https://floating-ui.com/docs/usetransition#usetransitionstyles
 * @param transitionStatusConfig - It handles the transition duration and styles through css - https://floating-ui.com/docs/usetransition#usetransitionstatus
 */
export const useTransitions = ({
  context,
  transitionStylesConfig,
  transitionStatusConfig,
}: UseTransitionsProps): {
  transitionStyles: ReturnType<typeof useTransitionStyles>;
  transitionStatus: ReturnType<typeof useTransitionStatus>;
} => {
  // Set the transition styles and control animation through the transition styles
  const { isMounted: isMountedByStyles, styles } = useTransitionStyles(context, {
    ...transitionStylesConfig,
  });

  // Set the transition status and control animation through the transition status
  const { isMounted: isMountedByStatus, status } = useTransitionStatus(context, {
    ...transitionStatusConfig,
  });

  const transitionStyles = { isMounted: isMountedByStyles, styles };
  const transitionStatus = { isMounted: isMountedByStatus, status };

  return { transitionStyles, transitionStatus };
};
